import { webApi } from '@mi-front/api-clients';

/** Chip values for the stream-quality station status filter (multi-select, OR semantics). */
export const QUALITY_STATION_STATUS_FILTER_NO_INCIDENTS = 'no_incidents';
export const QUALITY_STATION_STATUS_FILTER_POOR_QUALITY = 'poor_quality';
export const QUALITY_STATION_STATUS_FILTER_SILENT = 'silent';
export const QUALITY_STATION_STATUS_FILTER_DISCONTINUITY = 'discontinuity';

export const QUALITY_STATION_STATUS_FILTER_VALUES = [
  QUALITY_STATION_STATUS_FILTER_NO_INCIDENTS,
  QUALITY_STATION_STATUS_FILTER_POOR_QUALITY,
  QUALITY_STATION_STATUS_FILTER_SILENT,
  QUALITY_STATION_STATUS_FILTER_DISCONTINUITY,
] as const;

export type StationStatusFilterValue = (typeof QUALITY_STATION_STATUS_FILTER_VALUES)[number];

/**
 * Returns true if the station row matches at least one selected filter bucket (union / OR).
 * Uses `stats` counts from the API; missing row is treated as all-zero (only "no incidents").
 */
export function stationMatchesStatusFilters(
  row: webApi.StreamQualityPerStationDto | undefined,
  selected: StationStatusFilterValue[],
): boolean {
  const stats = row?.stats;
  const gapCount = stats?.streamGapCount ?? 0;
  const poorCount = stats?.poorQualityIssueCount ?? 0;
  const silentCount = stats?.silentIssueCount ?? 0;

  const noIncidents = gapCount === 0 && poorCount === 0 && silentCount === 0;
  const poorQuality = poorCount > 0;
  const silent = silentCount > 0;
  const discontinuity = gapCount > 0;

  for (const key of selected) {
    if (key === QUALITY_STATION_STATUS_FILTER_NO_INCIDENTS && noIncidents) {
      return true;
    }
    if (key === QUALITY_STATION_STATUS_FILTER_POOR_QUALITY && poorQuality) {
      return true;
    }
    if (key === QUALITY_STATION_STATUS_FILTER_SILENT && silent) {
      return true;
    }
    if (key === QUALITY_STATION_STATUS_FILTER_DISCONTINUITY && discontinuity) {
      return true;
    }
  }
  return false;
}

/**
 * Filters `report.data.stationIds` in order to those matching the selected status chips.
 */
export function getFilteredStationIdsForReport(
  report: webApi.StreamQualityReportResponseDto,
  selected: StationStatusFilterValue[],
): string[] {
  const rowsById = new Map<string, webApi.StreamQualityPerStationDto>();
  for (const row of report.result.byStation) {
    rowsById.set(row.stationId, row);
  }

  return report.data.stationIds.filter((id) =>
    stationMatchesStatusFilters(rowsById.get(id), selected),
  );
}
